export const M9_UNIT_11 = {
  id: "m9-u11-time-clauses",
  title: "Unidad 11: Planning Future Events",
  topic: "Master time sequences! Learn to talk about future plans with 'when' and 'as soon as' to describe what happens first and what happens next!",
  iconType: "calendar",
  grammarImage: "/grammar/grammar-m9-u11.jpg",
  grammarText: "SUBORDINATE TIME CLAUSES: WHEN/AS SOON AS + present simple, WILL + verb. 'When it stops raining, I'll go out!' Use PRESENT for the first event, even though it's future!",
  
  sets: [
    // SET 1
    {
      id: "m9-u11-s1",
      title: "Set 1: Basic Time Sequences - When & As Soon As 📅",
      context: `**Project Manager's Meeting**

Manager: "When we finish the design phase, we'll start construction. As soon as the materials arrive, the builders will begin work."`,
      
      questions: [
        {
          type: "mc",
          q: "Which shows a future sequence correctly?",
          options: [
            "When it will stop raining, I'll go out",
            "When it stops raining, I'll go out",
            "When it stops raining, I go out"
          ],
          a: "When it stops raining, I'll go out",
          tip: "Use PRESENT simple after 'when', even for future!",
          successtip: "✓ PRESENT FOR FUTURE!\nAfter WHEN/AS SOON AS, use present simple, NOT 'will'! First event = present! 🎯"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cuando termine mi trabajo, te llamaré'",
          words: ["When", "I", "finish", "my", "work", "I'll", "call", "you"],
          a: "When I finish my work I'll call you",
          extra: ["will", "finished"],
          tip: "After 'when', NO uses 'will'!",
          successtip: "✓ SEQUENCE!\nPrimero: 'I finish' (presente). Segundo: 'I'll call' (futuro con will)! 📞"
        },
        {
          type: "mc",
          q: "What's the difference between 'when' and 'as soon as'?",
          options: [
            "'As soon as' means immediately after",
            "They're exactly the same",
            "'When' is more urgent"
          ],
          a: "'As soon as' means immediately after",
          tip: "'As soon as' = más inmediato!",
          successtip: "✓ AS SOON AS!\n'As soon as' emphasizes IMMEDIACY - right after the first event! ⚡"
        },
        {
          type: "input",
          q: "Complete: 'As soon as she _____, we'll start the meeting.' (arrive)",
          a: "arrives",
          tip: "Third person singular needs -s!",
          successtip: "✓ ARRIVES!\nPresent simple after 'as soon as'! Don't forget the -s! 👥"
        },
        {
          type: "match",
          q: "Match the first event with the second:",
          matches: [
            { left: "When the sun sets,", right: "it'll get dark" },
            { left: "When she arrives,", right: "I'll tell her" },
            { left: "When it stops raining,", right: "we'll go out" }
          ],
          successtip: "✓ SEQUENCES!\nFirst event (present simple), then result (will + verb)! Perfect pattern! 🔗"
        },
        {
          type: "traduce",
          q: "Traduce: 'Tan pronto como llegue el tren, subiremos'",
          words: ["As", "soon", "as", "the", "train", "arrives", "we'll", "get", "on"],
          a: "As soon as the train arrives we'll get on",
          extra: ["will", "arrive"],
          successtip: "✓ GET ON!\nGET ON = subir (a transporte). AS SOON AS = inmediatamente cuando! 🚂"
        },
        {
          type: "mc",
          q: "Which is INCORRECT?",
          options: [
            "When I finish, I'll call you",
            "When I'll finish, I'll call you",
            "As soon as I finish, I'll call you"
          ],
          a: "When I'll finish, I'll call you",
          tip: "NEVER use 'will' after when/as soon as!",
          successtip: "✓ NO WILL!\nNEVER 'when I will' or 'as soon as I will'! Always present simple! ❌"
        },
        {
          type: "input",
          q: "Complete: 'When you _____ home, will you make dinner?' (get)",
          a: "get",
          successtip: "✓ QUESTION FORM!\nYou can make questions with this pattern too! When + present, will + subject? 🤔"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cuando termines, iremos a casa'",
          words: ["When", "you", "finish", "we'll", "go", "home"],
          a: "When you finish we'll go home",
          extra: ["will", "finished"],
          successtip: "✓ FUTURE PLAN!\nCuando termines (presente) = first action. We'll go (futuro) = second action! 🏠"
        },
        {
          type: "mc",
          q: "What tense follows 'when' for future events?",
          options: [
            "Future with will",
            "Present simple",
            "Past simple"
          ],
          a: "Present simple",
          tip: "Even though it's future, use present!",
          successtip: "✓ PRESENT!\nThis is the KEY rule: WHEN/AS SOON AS + PRESENT (even for future events)! 🔑"
        }
      ]
    },

    // SET 2
    {
      id: "m9-u11-s2",
      title: "Set 2: Building & Construction Vocabulary 🏗️",
      context: `**Construction Schedule**

"First, we'll prepare the site. When the foundation is ready, we'll start the walls. As soon as the roof is on, we'll begin interior work."`,
      
      questions: [
        {
          type: "mc",
          q: "What do 'builders' do?",
          options: [
            "They design buildings",
            "They construct buildings",
            "They destroy buildings"
          ],
          a: "They construct buildings",
          successtip: "✓ BUILDERS!\nBuilders = constructores. They physically BUILD (construir) structures! 👷"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cuando pongan el techo, empezaremos dentro'",
          words: ["When", "they", "put", "on", "the", "roof", "we'll", "start", "inside"],
          a: "When they put on the roof we'll start inside",
          extra: ["will", "puts"],
          successtip: "✓ PUT ON!\nPUT ON the roof = instalar el techo. Essential construction vocabulary! 🏠"
        },
        {
          type: "input",
          q: "Complete: 'As soon as the _____ dries, we can paint.' (plaster)",
          a: "plaster",
          tip: "Plaster = yeso para paredes!",
          successtip: "✓ PLASTER!\nPlaster = yeso/enlucido. Must dry before painting! Construction sequence! 🎨"
        },
        {
          type: "match",
          q: "Match the construction stage:",
          matches: [
            { left: "Foundation", right: "Base of building" },
            { left: "Walls", right: "Vertical structures" },
            { left: "Roof", right: "Top covering" }
          ],
          successtip: "✓ BUILDING STAGES!\nFoundation → Walls → Roof. Logical construction sequence! 🏗️"
        },
        {
          type: "mc",
          q: "What does 'install' mean in construction?",
          options: [
            "To remove something",
            "To put something in place",
            "To break something"
          ],
          a: "To put something in place",
          tip: "Install = instalar, colocar!",
          successtip: "✓ INSTALL!\nInstall = put equipment/systems in place. Very common in construction! 🔧"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cuando los electricistas terminen, instalaremos las luces'",
          words: ["When", "the", "electricians", "finish", "we'll", "install", "the", "lights"],
          a: "When the electricians finish we'll install the lights",
          extra: ["will", "finished"],
          successtip: "✓ ELECTRICIANS!\nElectricians = electricistas. First wiring, THEN lights! Sequence! 💡"
        },
        {
          type: "input",
          q: "Complete: 'The _____ will fix the pipes before the plumber installs sinks.' (plumber)",
          a: "plumber",
          tip: "Plumber = fontanero/plomero!",
          successtip: "✓ PLUMBER!\nPlumber = fontanero. Works with pipes, water systems! Essential worker! 🚰"
        },
        {
          type: "match",
          q: "Match the worker to their job:",
          matches: [
            { left: "Electrician", right: "Wires and lights" },
            { left: "Plumber", right: "Pipes and water" },
            { left: "Carpenter", right: "Wood and furniture" }
          ],
          successtip: "✓ CONSTRUCTION WORKERS!\nEach specialist has their role in building! Teamwork! 👷‍♀️"
        },
        {
          type: "mc",
          q: "What does 'renovate' mean?",
          options: [
            "To build something new",
            "To repair and improve something old",
            "To destroy a building"
          ],
          a: "To repair and improve something old",
          tip: "Renovate = renovar!",
          successtip: "✓ RENOVATE!\nRenovate = make old things new again. Very common home/building word! 🏡"
        },
        {
          type: "traduce",
          q: "Traduce: 'Tan pronto como instalemos la cocina, podremos cocinar'",
          words: ["As", "soon", "as", "we", "install", "the", "kitchen", "we", "can", "cook"],
          a: "As soon as we install the kitchen we can cook",
          extra: ["will", "installs"],
          successtip: "✓ INSTALL THE KITCHEN!\nKitchen installation is a major project milestone! Then you can cook! 🍳"
        }
      ]
    },

    // SET 3
    {
      id: "m9-u11-s3",
      title: "Set 3: Reversing the Order - Flexibility! 🔄",
      context: `**Two Ways to Say It**

"When it rains, I stay inside." = "I stay inside when it rains."
Same meaning! You can reverse the order without the comma!`,
      
      questions: [
        {
          type: "mc",
          q: "Which is correct when reversing the sentence?",
          options: [
            "I'll call you, when I arrive",
            "I'll call you when I arrive",
            "I'll call you when, I arrive"
          ],
          a: "I'll call you when I arrive",
          tip: "NO comma when starting with the result!",
          successtip: "✓ NO COMMA!\nWhen the RESULT comes first, NO comma before 'when'! Different from Spanish! ✍️"
        },
        {
          type: "traduce",
          q: "Traduce (starting with result): 'Iré al gimnasio cuando termine el trabajo'",
          words: ["I'll", "go", "to", "the", "gym", "when", "I", "finish", "work"],
          a: "I'll go to the gym when I finish work",
          extra: ["will", "comma"],
          successtip: "✓ REVERSED ORDER!\nResult first (I'll go), condition second (when I finish). No comma! 🏋️"
        },
        {
          type: "input",
          q: "Complete without comma: 'We'll eat _____ they arrive.' (when)",
          a: "when",
          tip: "Result first = no comma needed!",
          successtip: "✓ FLEXIBLE WORD ORDER!\nBoth orders work! With/without comma depends on which comes first! 🍽️"
        },
        {
          type: "match",
          q: "Match equivalent sentences:",
          matches: [
            { left: "When I'm ready, I'll go", right: "I'll go when I'm ready" },
            { left: "As soon as it ends, we'll leave", right: "We'll leave as soon as it ends" },
            { left: "When you call, I'll answer", right: "I'll answer when you call" }
          ],
          successtip: "✓ SAME MEANING!\nBoth orders express the same idea! Choose based on emphasis! 🔄"
        },
        {
          type: "mc",
          q: "When does the sentence need a comma?",
          options: [
            "Always with 'when'",
            "When 'when/as soon as' comes first",
            "Never"
          ],
          a: "When 'when/as soon as' comes first",
          tip: "Comma separates time clause from result!",
          successtip: "✓ COMMA RULE!\nIF time clause comes FIRST → comma. IF result comes first → NO comma! 📝"
        },
        {
          type: "traduce",
          q: "Reescribe SIN coma: 'When she arrives, I'll tell her' → 'I'll tell her...'",
          words: ["I'll", "tell", "her", "when", "she", "arrives"],
          a: "I'll tell her when she arrives",
          extra: ["comma", "will"],
          successtip: "✓ RESTRUCTURED!\nSame meaning, different emphasis! Both correct, comma rule changes! 💬"
        },
        {
          type: "input",
          q: "Which word is missing? 'He'll leave _____ the meeting finishes.'",
          a: "when",
          tip: "Could also be 'as soon as'!",
          successtip: "✓ CONNECTORS!\nBoth 'when' and 'as soon as' work here! 'As soon as' = more immediate! ⏰"
        },
        {
          type: "match",
          q: "Add comma or no comma?",
          matches: [
            { left: "When it rains, ___ I stay home", right: "COMMA before 'I'" },
            { left: "I stay home ___ when it rains", right: "NO COMMA before 'when'" },
            { left: "As soon as I'm ready, ___ I'll go", right: "COMMA before 'I'" }
          ],
          successtip: "✓ PUNCTUATION MASTERY!\nTime clause first = COMMA. Result first = NO COMMA! Simple rule! ✅"
        },
        {
          type: "mc",
          q: "Which sounds more urgent/immediate?",
          options: [
            "When I finish, I'll help you",
            "As soon as I finish, I'll help you",
            "They're exactly the same"
          ],
          a: "As soon as I finish, I'll help you",
          tip: "'As soon as' = inmediatamente!",
          successtip: "✓ EMPHASIS!\n'As soon as' stresses IMMEDIACY more than 'when'! Small but important difference! ⚡"
        },
        {
          type: "traduce",
          q: "Traduce (result first): 'Compraremos muebles cuando tengamos dinero'",
          words: ["We'll", "buy", "furniture", "when", "we", "have", "money"],
          a: "We'll buy furniture when we have money",
          extra: ["will", "comma"],
          successtip: "✓ NO COMMA!\nResult first (We'll buy) → no comma before 'when we have'! 🛋️"
        }
      ]
    },

    // SET 4
    {
      id: "m9-u11-s4",
      title: "Set 4: Present Perfect Alternative - Advanced! 🎓",
      context: `**More Formal/British Style**

"When the paint HAS dried, we'll hang pictures."
You can use present perfect instead of present simple to emphasize completion!`,
      
      questions: [
        {
          type: "mc",
          q: "Which is MORE formal/British?",
          options: [
            "When it stops raining, we'll go",
            "When it has stopped raining, we'll go",
            "When it stopped raining, we'll go"
          ],
          a: "When it has stopped raining, we'll go",
          tip: "Present perfect = more formal!",
          successtip: "✓ PRESENT PERFECT!\nUK English often uses 'has/have + past participle' for emphasis on completion! 🇬🇧"
        },
        {
          type: "traduce",
          q: "Traduce (estilo formal): 'Cuando haya terminado, te llamaré'",
          words: ["When", "I", "have", "finished", "I'll", "call", "you"],
          a: "When I have finished I'll call you",
          extra: ["has", "finish"],
          successtip: "✓ HAVE FINISHED!\nPresent perfect emphasizes COMPLETION of the first action! More formal! ✨"
        },
        {
          type: "input",
          q: "Complete formal: 'As soon as she _____ arrived, we'll start.' (have)",
          a: "has",
          tip: "Third person = 'has'!",
          successtip: "✓ HAS ARRIVED!\n'Has arrived' = present perfect. Emphasizes completion before starting! 📍"
        },
        {
          type: "match",
          q: "Match simple with perfect forms:",
          matches: [
            { left: "When I finish...", right: "When I have finished..." },
            { left: "When she arrives...", right: "When she has arrived..." },
            { left: "When they leave...", right: "When they have left..." }
          ],
          successtip: "✓ BOTH CORRECT!\nPresent simple = common. Present perfect = formal/British. Both work! 🎯"
        },
        {
          type: "mc",
          q: "Why use present perfect here?",
          options: [
            "To sound more polite/formal",
            "It's required grammar",
            "It changes the meaning completely"
          ],
          a: "To sound more polite/formal",
          tip: "It's optional, not required!",
          successtip: "✓ STYLE CHOICE!\nBoth forms correct! Present perfect = more formal register. Choose your style! 🎩"
        },
        {
          type: "traduce",
          q: "Traduce (formal): 'Cuando hayan comido, lavaremos los platos'",
          words: ["When", "they", "have", "eaten", "we'll", "wash", "the", "dishes"],
          a: "When they have eaten we'll wash the dishes",
          extra: ["has", "ate"],
          successtip: "✓ HAVE EATEN!\nPresent perfect (have eaten) emphasizes they're DONE eating first! 🍽️"
        },
        {
          type: "input",
          q: "Complete: 'We'll go out when the rain _____ stopped.' (have)",
          a: "has",
          tip: "'The rain' = it = has!",
          successtip: "✓ HAS STOPPED!\n'The rain has stopped' = present perfect. Rain (it) is third person singular! ☔"
        },
        {
          type: "match",
          q: "Choose the register/style:",
          matches: [
            { left: "When I finish work...", right: "Neutral/Common" },
            { left: "When I have finished work...", right: "Formal/British" },
            { left: "When I finished work...", right: "INCORRECT (past!)" }
          ],
          successtip: "✓ REGISTER AWARENESS!\nPresent simple = everyday. Present perfect = formal. Past = wrong! ❌"
        },
        {
          type: "mc",
          q: "Which is INCORRECT?",
          options: [
            "When they arrive, we'll eat",
            "When they have arrived, we'll eat",
            "When they will arrive, we'll eat"
          ],
          a: "When they will arrive, we'll eat",
          tip: "NEVER 'will' after 'when'!",
          successtip: "✓ NO WILL!\nPresent simple ✅, Present perfect ✅, 'Will' after when/as soon as ❌ NEVER! 🚫"
        },
        {
          type: "traduce",
          q: "Traduce (formal): 'Tan pronto como haya llegado el autobús, subiremos'",
          words: ["As", "soon", "as", "the", "bus", "has", "arrived", "we'll", "get", "on"],
          a: "As soon as the bus has arrived we'll get on",
          extra: ["have", "will"],
          successtip: "✓ ADVANCED STRUCTURE!\n'As soon as + present perfect' = very formal/British style! Excellent! 🚌"
        }
      ]
    },

    // SET 5
    {
      id: "m9-u11-s5",
      title: "Set 5: Real-Life Planning Situations 📋",
      context: `**Making Plans**

Friend 1: "When do you want to meet?"
Friend 2: "As soon as I finish work, I'll call you. When you're ready, we'll go!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which plan is correctly stated?",
          options: [
            "When I will get paid, I'll buy it",
            "When I get paid, I'll buy it",
            "When I got paid, I'll buy it"
          ],
          a: "When I get paid, I'll buy it",
          tip: "Present simple after 'when'!",
          successtip: "✓ MONEY PLAN!\nCommon real-life sentence! First: get paid. Then: buy something! 💰"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cuando ahorre suficiente dinero, compraré un coche'",
          words: ["When", "I", "save", "enough", "money", "I'll", "buy", "a", "car"],
          a: "When I save enough money I'll buy a car",
          extra: ["will", "saved"],
          successtip: "✓ SAVINGS GOAL!\nPerfect for talking about future financial goals! Super useful! 🚗"
        },
        {
          type: "input",
          q: "Complete: 'As soon as the store _____, I'll go shopping.' (open)",
          a: "opens",
          tip: "Third person needs -s!",
          successtip: "✓ OPENS!\n'The store opens' = present simple, third person. Then shopping! 🛍️"
        },
        {
          type: "match",
          q: "Match realistic plans:",
          matches: [
            { left: "When I graduate,", right: "I'll look for a job" },
            { left: "When the movie ends,", right: "we'll go home" },
            { left: "When she wakes up,", right: "I'll tell her the news" }
          ],
          successtip: "✓ EVERYDAY PLANS!\nThese time clauses are perfect for making real-life plans! So practical! 📅"
        },
        {
          type: "mc",
          q: "What's the best way to express: 'First vacation, then buy house'?",
          options: [
            "When we go on vacation, we'll buy a house",
            "After our vacation, we'll buy a house",
            "When we buy a house, we'll go on vacation"
          ],
          a: "After our vacation, we'll buy a house",
          tip: "Order matters! Vacation FIRST!",
          successtip: "✓ SEQUENCE MATTERS!\n'After' shows sequence clearly. Time clauses show what happens first! 🏖️🏠"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cuando encuentre mis llaves, saldré'",
          words: ["When", "I", "find", "my", "keys", "I'll", "leave"],
          a: "When I find my keys I'll leave",
          extra: ["will", "found"],
          successtip: "✓ COMMON SITUATION!\nLost keys = can't leave. Find keys = can leave! Perfect sequence! 🔑"
        },
        {
          type: "input",
          q: "Complete: 'I'll make dinner when I _____ home.' (get)",
          a: "get",
          tip: "'Get home' = llegar a casa!",
          successtip: "✓ GET HOME!\n'Get home' = arrive at home. Then cook! Typical evening plan! 🏠🍳"
        },
        {
          type: "match",
          q: "Match work/school plans:",
          matches: [
            { left: "When the bell rings,", right: "class will end" },
            { left: "When the boss arrives,", right: "we'll start the meeting" },
            { left: "When I finish this project,", right: "I'll take a break" }
          ],
          successtip: "✓ WORK & SCHOOL!\nTime clauses are essential for coordinating activities with others! 💼"
        },
        {
          type: "mc",
          q: "Which expresses urgency/immediacy best?",
          options: [
            "When the ambulance arrives, call me",
            "As soon as the ambulance arrives, call me",
            "If the ambulance arrives, call me"
          ],
          a: "As soon as the ambulance arrives, call me",
          tip: "'As soon as' = immediately!",
          successtip: "✓ URGENCY!\n'As soon as' = right away, immediately. Perfect for urgent situations! 🚑"
        },
        {
          type: "traduce",
          q: "Traduce: 'Te enviaré el documento tan pronto como lo termine'",
          words: ["I'll", "send", "you", "the", "document", "as", "soon", "as", "I", "finish", "it"],
          a: "I'll send you the document as soon as I finish it",
          extra: ["will", "finished"],
          successtip: "✓ WORK PROMISE!\nPerfect for professional communication! Shows clear timeline! 📧"
         }
      ]
    },


     // SET 6
    {
      id: "m9-u11-s6",
      title: "Set 6: Questions with Time Clauses ❓",
      context: `**Making Arrangements**

Friend: "When you get home, will you call me?"
You: "Yes! As soon as I arrive, I'll send you a message."`,
      
      questions: [
        {
          type: "mc",
          q: "Which question is correct?",
          options: [
            "When you will arrive, will you call?",
            "When you arrive, will you call?",
            "When you arrive, you will call?"
          ],
          a: "When you arrive, will you call?",
          tip: "Present simple after 'when', then question with 'will'!",
          successtip: "✓ QUESTION FORM!\nWHEN + present simple + comma, WILL + subject + verb? Perfect structure! 📞"
        },
        {
          type: "traduce",
          q: "Traduce: '¿Me ayudarás cuando termine?'",
          words: ["Will", "you", "help", "me", "when", "I", "finish"],
          a: "Will you help me when I finish",
          extra: ["will", "finished"],
          tip: "Result first = no comma!",
          successtip: "✓ QUESTION!\n'Will you...when I...' = asking about future help with time condition! 🤝"
        },
        {
          type: "input",
          q: "Complete: 'When the train _____, will we get on?' (arrive)",
          a: "arrives",
          tip: "Third person needs -s!",
          successtip: "✓ ARRIVES!\nQuestions work the same: WHEN + present, WILL + question! 🚂"
        },
        {
          type: "match",
          q: "Match the question beginnings and endings:",
          matches: [
            { left: "When you see him,", right: "will you tell him?" },
            { left: "As soon as it stops raining,", right: "will we leave?" },
            { left: "When she finishes work,", right: "will she come?" }
          ],
          successtip: "✓ QUESTION PATTERNS!\nSame grammar as statements, just add question form at the end! 💬"
        },
        {
          type: "mc",
          q: "Which is the most natural way to ask?",
          options: [
            "Will you call me when you arrive?",
            "When you arrive, will you call me?",
            "Both are equally natural"
          ],
          a: "Both are equally natural",
          tip: "Both orders work for questions!",
          successtip: "✓ FLEXIBLE!\nBoth orders work! Choose based on emphasis and conversation flow! 🔄"
        },
        {
          type: "traduce",
          q: "Traduce: '¿Cuándo empezaremos cuando lleguen?'",
          words: ["When", "will", "we", "start", "when", "they", "arrive"],
          a: "When will we start when they arrive",
          extra: ["started", "will"],
          tip: "Two 'whens' with different meanings!",
          successtip: "✓ TWO WHENS!\nFirst 'when' = question word. Second 'when' = time conjunction! Tricky! 🤔"
        },
        {
          type: "input",
          q: "Complete: 'As soon as the meeting ends, _____ you come?' (will)",
          a: "will",
          successtip: "✓ WILL!\nQuestion form needs 'will' + subject after the comma! Standard pattern! ✅"
        },
        {
          type: "match",
          q: "Match questions to likely situations:",
          matches: [
            { left: "When you get paid, will you buy it?", right: "Shopping decision" },
            { left: "When the food arrives, will you eat?", right: "Meal planning" },
            { left: "When the sun sets, will we leave?", right: "Evening plans" }
          ],
          successtip: "✓ REAL QUESTIONS!\nThese questions help coordinate plans and decisions! Super practical! 🎯"
        },
        {
          type: "mc",
          q: "What's the answer form for: 'When you're ready, will you call?'",
          options: [
            "Yes, I call you",
            "Yes, I'll call you",
            "Yes, when I'm ready"
          ],
          a: "Yes, I'll call you",
          tip: "Answer with 'will' (future)!",
          successtip: "✓ ANSWER!\nQuestion = 'will you...?' Answer = 'Yes, I will/I'll...' Simple! ✅"
        },
        {
          type: "traduce",
          q: "Traduce: '¿Nos dirás cuando sepas la respuesta?'",
          words: ["Will", "you", "tell", "us", "when", "you", "know", "the", "answer"],
          a: "Will you tell us when you know the answer",
          extra: ["will", "knew"],
          successtip: "✓ PERFECT QUESTION!\nAsking someone to share information once they have it! Common pattern! 💡"
        }
      ]
    },

    // SET 7
    {
      id: "m9-u11-s7",
      title: "Set 7: Negative Forms - Won't Sentences 🚫",
      context: `**Making Promises**

"I won't leave until you arrive. When you get here, we'll go together. I won't start without you!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which negative is correct?",
          options: [
            "When it rains, I don't go out",
            "When it rains, I won't go out",
            "Both are correct"
          ],
          a: "When it rains, I won't go out",
          tip: "Use WON'T for future result!",
          successtip: "✓ WON'T!\nWON'T = will not. Use for negative future result after time clause! 🚫"
        },
        {
          type: "traduce",
          q: "Traduce: 'No saldré hasta que dejes de llover'",
          words: ["I", "won't", "go", "out", "until", "it", "stops", "raining"],
          a: "I won't go out until it stops raining",
          extra: ["will", "don't"],
          tip: "'Until' = hasta que!",
          successtip: "✓ UNTIL!\nUNTIL works like WHEN! UNTIL + present simple, WON'T + verb! ⏰"
        },
        {
          type: "input",
          q: "Complete: 'We _____ start until everyone arrives.' (won't)",
          a: "won't",
          tip: "Won't = will not!",
          successtip: "✓ WON'T START!\nPromising NOT to do something until condition is met! Important pattern! ⏸️"
        },
        {
          type: "match",
          q: "Match the negative statements:",
          matches: [
            { left: "When she calls,", right: "I won't answer" },
            { left: "Until you finish,", right: "we won't leave" },
            { left: "As soon as it's dark,", right: "I won't go out" }
          ],
          successtip: "✓ NEGATIVE PLANS!\nWON'T expresses what you're NOT going to do! Clear boundaries! 🛑"
        },
        {
          type: "mc",
          q: "What does 'until' mean in time clauses?",
          options: [
            "Before something happens",
            "Up to the time when something happens",
            "After something happens"
          ],
          a: "Up to the time when something happens",
          tip: "Until = hasta que!",
          successtip: "✓ UNTIL!\nUNTIL = something continues up to a certain point, then stops/changes! ⏱️"
        },
        {
          type: "traduce",
          q: "Traduce: 'No empezaré hasta que estés listo'",
          words: ["I", "won't", "start", "until", "you're", "ready"],
          a: "I won't start until you're ready",
          extra: ["will", "don't"],
          successtip: "✓ WAITING!\nShowing respect/consideration - won't start until others are ready! 🤝"
        },
        {
          type: "input",
          q: "Complete: 'She _____ eat until the food cools down.' (won't)",
          a: "won't",
          successtip: "✓ WON'T EAT!\nWaiting for condition (food cooling) before action (eating)! Sensible! 🍲"
        },
        {
          type: "match",
          q: "Match 'until' situations:",
          matches: [
            { left: "I won't sleep until", right: "you come home" },
            { left: "They won't eat until", right: "everyone arrives" },
            { left: "He won't leave until", right: "the job is done" }
          ],
          successtip: "✓ UNTIL PATTERNS!\nUNTIL shows patience, waiting, or conditions that must be met first! ⏳"
        },
        {
          type: "mc",
          q: "Which is MORE emphatic/strong?",
          options: [
            "When you're ready, I'll go",
            "I won't go until you're ready",
            "They're exactly the same"
          ],
          a: "I won't go until you're ready",
          tip: "'Won't...until' = stronger promise!",
          successtip: "✓ EMPHASIS!\n'Won't...until' emphasizes the promise/condition more strongly! 💪"
        },
        {
          type: "traduce",
          q: "Traduce: 'No te llamaré hasta que termine mi reunión'",
          words: ["I", "won't", "call", "you", "until", "I", "finish", "my", "meeting"],
          a: "I won't call you until I finish my meeting",
          extra: ["will", "don't"],
          successtip: "✓ CLEAR BOUNDARY!\nSetting clear expectations - call will come AFTER meeting! Professional! 📱"
        }
      ]
    },

    // SET 8
    {
      id: "m9-u11-s8",
      title: "Set 8: Multiple Actions - Complex Sequences ⛓️",
      context: `**Project Timeline**

"When we buy the materials, we'll start construction. As soon as the walls are up, the electrician will come. When he finishes, the plumber will start..."`,
      
      questions: [
        {
          type: "mc",
          q: "In a sequence of 3 events, which is correct?",
          options: [
            "When A happens, B will happen, then C will happen",
            "When A will happen, B happens, C happens",
            "When A happens, B happens, C happens"
          ],
          a: "When A happens, B will happen, then C will happen",
          tip: "First event = present, following = future!",
          successtip: "✓ SEQUENCE!\nFirst condition = present. All results = future (will)! Chain of events! 🔗"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cuando él termine, ella empezará, y luego yo ayudaré'",
          words: ["When", "he", "finishes", "she'll", "start", "and", "then", "I'll", "help"],
          a: "When he finishes she'll start and then I'll help",
          extra: ["will", "finish"],
          successtip: "✓ THREE STEPS!\nComplex sequence: condition + result 1 + result 2! Advanced planning! 📋"
        },
        {
          type: "input",
          q: "Complete: 'When the paint _____, I'll hang pictures, then we'll arrange furniture.' (dry)",
          a: "dries",
          tip: "Third person singular!",
          successtip: "✓ MULTIPLE RESULTS!\nOne condition (paint dries) → two results (hang + arrange)! 🎨🖼️"
        },
        {
          type: "match",
          q: "Match complex sequences:",
          matches: [
            { left: "When I wake up,", right: "I'll shower, then eat breakfast" },
            { left: "When she arrives,", right: "we'll talk, then go out" },
            { left: "When it stops raining,", right: "we'll walk, then have coffee" }
          ],
          successtip: "✓ DAILY ROUTINES!\nTime clauses perfect for describing multi-step daily activities! ☀️"
        },
        {
          type: "mc",
          q: "How do you connect multiple future actions?",
          options: [
            "Use 'will' for all after the time clause",
            "Use present simple for all",
            "Mix present and future randomly"
          ],
          a: "Use 'will' for all after the time clause",
          tip: "After comma, all actions are future!",
          successtip: "✓ ALL FUTURE!\nAfter the time clause, ALL actions use 'will'! They're all results! 🎯"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cuando llegue a casa, cocinaré, comeré y luego descansaré'",
          words: ["When", "I", "get", "home", "I'll", "cook", "eat", "and", "then", "rest"],
          a: "When I get home I'll cook eat and then rest",
          extra: ["will", "got"],
          successtip: "✓ EVENING PLAN!\nThree activities after one condition! Perfect for planning your evening! 🏠"
        },
        {
          type: "input",
          q: "Complete: 'As soon as the meeting ends, we'll _____, then celebrate.' (present)",
          a: "present",
          tip: "Present the results, then celebrate!",
          successtip: "✓ BUSINESS SEQUENCE!\nMeeting → presentation → celebration! Clear project timeline! 🎉"
        },
        {
          type: "match",
          q: "Match work sequences:",
          matches: [
            { left: "When the design is ready,", right: "we'll print samples, then test them" },
            { left: "When the client approves,", right: "we'll start production, then deliver" },
            { left: "When the report is done,", right: "I'll review it, then send it" }
          ],
          successtip: "✓ PROJECT MANAGEMENT!\nTime clauses essential for coordinating work sequences! 💼"
        },
        {
          type: "mc",
          q: "Which correctly shows: wake → shower → dress → leave?",
          options: [
            "When I wake up, I shower, dress, and leave",
            "When I wake up, I'll shower, dress, and leave",
            "When I'll wake up, I'll shower, dress, and leave"
          ],
          a: "When I wake up, I'll shower, dress, and leave",
          tip: "Condition = present, actions = future!",
          successtip: "✓ MORNING ROUTINE!\nOne time marker (wake up), multiple future actions! Perfect! ☀️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cuando terminen de construir, pintaremos, decoraremos y nos mudaremos'",
          words: ["When", "they", "finish", "building", "we'll", "paint", "decorate", "and", "move", "in"],
          a: "When they finish building we'll paint decorate and move in",
          extra: ["will", "finished"],
          successtip: "✓ HOME PROJECT!\nConstruction → painting → decorating → moving! Complete renovation plan! 🏡"
        }
      ]
    },

    // SET 9
    {
      id: "m9-u11-s9",
      title: "Set 9: Before, After, Until - More Time Words ⏰",
      context: `**More Time Connectors**

"I'll finish this BEFORE I start that. AFTER the meeting, I'll call you. I'll wait UNTIL you're ready."
These also work like WHEN!`,
      
      questions: [
        {
          type: "mc",
          q: "Which follows the same rule as 'when'?",
          options: [
            "I'll call you before I will leave",
            "I'll call you before I leave",
            "I'll call you before I left"
          ],
          a: "I'll call you before I leave",
          tip: "BEFORE + present simple!",
          successtip: "✓ BEFORE!\nBEFORE works like WHEN! Use present simple after it, even for future! 📞"
        },
        {
          type: "traduce",
          q: "Traduce: 'Después de que coma, iré al gimnasio'",
          words: ["After", "I", "eat", "I'll", "go", "to", "the", "gym"],
          a: "After I eat I'll go to the gym",
          extra: ["will", "ate"],
          tip: "AFTER + present = future meaning!",
          successtip: "✓ AFTER!\nAFTER also uses present simple for future events! Same rule! 🏋️"
        },
        {
          type: "input",
          q: "Complete: 'I'll do the dishes _____ we finish eating.' (after)",
          a: "after",
          successtip: "✓ AFTER!\nAFTER shows sequence clearly - eating happens first, then dishes! 🍽️"
        },
        {
          type: "match",
          q: "Match time words to meanings:",
          matches: [
            { left: "BEFORE", right: "Earlier than" },
            { left: "AFTER", right: "Later than" },
            { left: "UNTIL", right: "Up to the time when" }
          ],
          successtip: "✓ TIME WORDS!\nEach word shows different time relationship! Precise sequencing! 🕐"
        },
        {
          type: "mc",
          q: "Which means 'I won't leave before you arrive'?",
          options: [
            "I'll wait until you arrive",
            "I'll leave when you arrive",
            "I'll leave after you arrive"
          ],
          a: "I'll wait until you arrive",
          tip: "UNTIL = hasta que!",
          successtip: "✓ UNTIL = STAYING!\nUNTIL means you'll stay/wait continuously until the event! ⏳"
        },
        {
          type: "traduce",
          q: "Traduce: 'Terminaré esto antes de empezar aquello'",
          words: ["I'll", "finish", "this", "before", "I", "start", "that"],
          a: "I'll finish this before I start that",
          extra: ["will", "finished"],
          successtip: "✓ PRIORITIES!\nBEFORE makes sequence clear - this task first, that task second! 📝"
        },
        {
          type: "input",
          q: "Complete: 'After the sun _____, we'll light the fire.' (set)",
          a: "sets",
          tip: "Third person singular!",
          successtip: "✓ SETS!\n'The sun sets' = present simple. Natural event as time marker! 🌅"
        },
        {
          type: "match",
          q: "Match sentences with time words:",
          matches: [
            { left: "Call me _____ you arrive", right: "when/as soon as" },
            { left: "Call me _____ you leave", right: "before" },
            { left: "Don't call _____ I'm ready", right: "until" }
          ],
          successtip: "✓ TIME PRECISION!\nDifferent words = different meanings! Choose carefully for clarity! 🎯"
        },
        {
          type: "mc",
          q: "Which is CORRECT?",
          options: [
            "After I will finish, I'll rest",
            "After I finish, I'll rest",
            "After I finished, I'll rest"
          ],
          a: "After I finish, I'll rest",
          tip: "Present simple after 'after'!",
          successtip: "✓ AFTER + PRESENT!\nAll time conjunctions use present simple for future! Consistent rule! ✅"
        },
        {
          type: "traduce",
          q: "Traduce: 'No comeré hasta que llegues'",
          words: ["I", "won't", "eat", "until", "you", "arrive"],
          a: "I won't eat until you arrive",
          extra: ["will", "don't"],
          successtip: "✓ POLITE WAITING!\nShowing consideration - won't start eating until everyone is there! 🍽️"
        }
      ]
    },

    // SET 10
    {
      id: "m9-u11-s10",
      title: "Set 10: Master Practice - Real Planning Scenarios 🎓",
      context: `**Final Challenge!**

Put it all together! Use when/as soon as/before/after/until to plan complex real-life situations like:
- Home renovation projects
- Travel plans
- Work coordination
- Daily schedules`,
      
      questions: [
        {
          type: "mc",
          q: "Which is the BEST way to coordinate: 'Don't start painting until the plaster is dry'?",
          options: [
            "When the plaster dries, we'll paint",
            "We won't paint until the plaster dries",
            "After the plaster will dry, we'll paint"
          ],
          a: "We won't paint until the plaster dries",
          tip: "'Won't...until' = strongest warning!",
          successtip: "✓ STRONG CONDITION!\n'Won't until' emphasizes the condition must be met first! Important! ⚠️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cuando el electricista termine, el fontanero puede empezar'",
          words: ["When", "the", "electrician", "finishes", "the", "plumber", "can", "start"],
          a: "When the electrician finishes the plumber can start",
          extra: ["will", "finished"],
          successtip: "✓ WORK COORDINATION!\nCoordinating different tradespeople - essential for construction! 👷"
        },
        {
          type: "input",
          q: "Complete travel plan: 'As soon as we _____ at the hotel, we'll check in, then explore.' (arrive)",
          a: "arrive",
          successtip: "✓ TRAVEL PLAN!\nComplete sequence: arrive → check in → explore! Perfect planning! ✈️"
        },
        {
          type: "match",
          q: "Match complex planning situations:",
          matches: [
            { left: "When the carpet is in,", right: "we'll move furniture, then decorate" },
            { left: "After we land,", right: "we'll get our bags, then take a taxi" },
            { left: "Before I leave work,", right: "I'll finish this, then email the client" }
          ],
          successtip: "✓ REAL SCENARIOS!\nThese are exactly how native speakers coordinate complex plans! 🌟"
        },
        {
          type: "mc",
          q: "Best way to say: 'First walls, then electricity, then plumbing'?",
          options: [
            "When we build walls, we'll do electricity and plumbing",
            "When the walls are up, the electrician will come. When he finishes, the plumber will start",
            "We'll build walls, do electricity, and plumbing"
          ],
          a: "When the walls are up, the electrician will come. When he finishes, the plumber will start",
          tip: "Show clear sequence with multiple time clauses!",
          successtip: "✓ DETAILED SEQUENCE!\nMultiple time clauses show clear step-by-step progression! Professional! 🏗️"
        },
        {
          type: "traduce",
          q: "Traduce plan completo: 'Cuando ahorre dinero, compraré un coche, luego viajaré'",
          words: ["When", "I", "save", "money", "I'll", "buy", "a", "car", "then", "travel"],
          a: "When I save money I'll buy a car then travel",
          extra: ["will", "saved"],
          successtip: "✓ LIFE GOALS!\nOne condition (save), two dreams (car + travel)! Perfect goal setting! 🎯"
        },
        {
          type: "input",
          q: "Complete: 'I won't make any decisions _____ I talk to my partner.' (until)",
          a: "until",
          successtip: "✓ RESPECTFUL!\nShowing partnership - no decisions without discussion! Excellent! 💑"
        },
        {
          type: "match",
          q: "Match professional situations:",
          matches: [
            { left: "Before the meeting starts,", right: "I'll prepare the slides" },
            { left: "When the client approves,", right: "we'll begin production" },
            { left: "After the project ends,", right: "we'll review and celebrate" }
          ],
          successtip: "✓ BUSINESS MASTERY!\nTime clauses essential for professional project management! 💼"
        },
        {
          type: "mc",
          q: "Which shows BOTH events are future but maintains correct grammar?",
          options: [
            "When I will graduate next year, I'll look for a job",
            "When I graduate next year, I'll look for a job",
            "When I graduated next year, I'll look for a job"
          ],
          a: "When I graduate next year, I'll look for a job",
          tip: "Present simple even with future time marker!",
          successtip: "✓ PRESENT FOR FUTURE!\nEven with 'next year', use present after 'when'! Rule is absolute! 🎓"
        },
        {
          type: "traduce",
          q: "Traduce escenario complejo: 'Tan pronto como el bebé duerma, cocinaremos, comeremos y luego limpiaremos'",
          words: ["As", "soon", "as", "the", "baby", "sleeps", "we'll", "cook", "eat", "and", "then", "clean"],
          a: "As soon as the baby sleeps we'll cook eat and then clean",
          extra: ["will", "slept"],
          successtip: "✓ PERFECT MASTERY!\nComplex real-life scenario with multiple actions! You've mastered time clauses! 🌟👶"
        }
      ]
    }

  ]
};
